import { usePermission } from "../hooks/usePermission";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import { Plus, X, Users, TrendingUp, DollarSign, Award, Phone, MessageCircle, MessageSquare, Mail, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

const CONTACT_METHODS = [
  { value: "WHATSAPP", label: "WhatsApp", color: "bg-green-100 text-green-700 border-green-200",   activeColor: "bg-green-500 text-white border-green-500",   icon: <MessageCircle size={13}/> },
  { value: "CALL",     label: "Call",     color: "bg-blue-100 text-blue-700 border-blue-200",       activeColor: "bg-blue-500 text-white border-blue-500",     icon: <Phone size={13}/> },
  { value: "CHAT",     label: "Chat",     color: "bg-purple-100 text-purple-700 border-purple-200", activeColor: "bg-purple-500 text-white border-purple-500", icon: <MessageSquare size={13}/> },
  { value: "EMAIL",    label: "Email",    color: "bg-yellow-100 text-yellow-700 border-yellow-200", activeColor: "bg-yellow-500 text-white border-yellow-500", icon: <Mail size={13}/> },
  { value: "OTHER",    label: "Other",    color: "bg-gray-100 text-gray-700 border-gray-200",       activeColor: "bg-gray-500 text-white border-gray-500",     icon: <MoreHorizontal size={13}/> },
];

const STATUS_OPTIONS = [
  { value: "NEW_LEAD",           label: "New Lead",            color: "bg-gray-100 text-gray-700" },
  { value: "INTERESTED",         label: "Interested",          color: "bg-blue-100 text-blue-700" },
  { value: "FOLLOW_UP_REQUIRED", label: "Follow-Up Required",  color: "bg-yellow-100 text-yellow-700" },
  { value: "NEGOTIATION",        label: "Negotiation",         color: "bg-orange-100 text-orange-700" },
  { value: "POTENTIAL_BUYER",    label: "Potential Buyer",     color: "bg-purple-100 text-purple-700" },
  { value: "NOT_INTERESTED",     label: "Not Interested",      color: "bg-red-100 text-red-700" },
  { value: "LOST_OPPORTUNITY",   label: "Lost Opportunity",    color: "bg-red-200 text-red-800" },
  { value: "CONVERTED",          label: "Converted to Client", color: "bg-green-100 text-green-700" },
  { value: "ACTIVE",             label: "Active",              color: "bg-teal-100 text-teal-700" },
];

const PIPELINE_STAGES = [
  { key: "NEW_LEAD",           label: "New Lead",        color: "bg-gray-400" },
  { key: "INTERESTED",         label: "Interested",      color: "bg-blue-400" },
  { key: "FOLLOW_UP_REQUIRED", label: "Follow-Up",       color: "bg-yellow-400" },
  { key: "NEGOTIATION",        label: "Negotiation",     color: "bg-orange-400" },
  { key: "POTENTIAL_BUYER",    label: "Potential Buyer", color: "bg-purple-400" },
  { key: "CONVERTED",          label: "Converted",       color: "bg-green-500" },
  { key: "NOT_INTERESTED",     label: "Not Interested",  color: "bg-red-300" },
  { key: "LOST_OPPORTUNITY",   label: "Lost",            color: "bg-red-500" },
];

const blankForm = { customerName: "", company: "", phone: "", email: "", notes: "", status: "NEW_LEAD", assignedToId: "", nextFollowUp: "", contactMethod: "" };

const StatusBadge = ({ status }: { status: string }) => {
  const s = STATUS_OPTIONS.find(o => o.value === status) || { label: status, color: "bg-gray-100 text-gray-700" };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
};

const ContactBadge = ({ value }: { value?: string }) => {
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  const m = CONTACT_METHODS.find(x => x.value === value);
  if (!m) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border ${m.color}`}>
      {m.icon} {m.label}
    </span>
  );
};

const CRM: React.FC = () => {
  const navigate = useNavigate();
  const canCreateCrm = usePermission("crm", "create");
  const canEditCrm   = usePermission("crm", "edit");
  const canDeleteCrm = usePermission("crm", "delete");
  const [customers, setCustomers] = useState<any[]>([]);
  const [stats,     setStats]     = useState<any>({});
  const [pipeline,  setPipeline]  = useState<any>({});
  const [users,     setUsers]     = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"customers"|"pipeline"|"reports">("customers");

  /* ── Form (add + edit) ─────────────────────────────── */
  const [showForm,    setShowForm]    = useState(false);
  const [editCustomer, setEditCustomer] = useState<any>(null); // null = add, object = edit
  const [form, setForm] = useState({ ...blankForm });

  /* ── Filters ───────────────────────────────────────── */
  const [filters, setFilters] = useState({
    status: "", assignedToId: "", company: "", followUpToday: false, contactMethod: "",
  });

  /* ── Load data ─────────────────────────────────────── */
  const load = useCallback((f = filters) => {
    const params: any = {};
    if (f.status)        params.status        = f.status;
    if (f.assignedToId)  params.assignedToId  = f.assignedToId;
    if (f.company)       params.company       = f.company;
    if (f.followUpToday) params.followUpToday = "true";
    if (f.contactMethod) params.contactMethod = f.contactMethod;
    api.get("/crm", { params }).then(r => setCustomers(r.data));
    api.get("/crm/dashboard").then(r => setStats(r.data)).catch(() => {});
    api.get("/crm/pipeline").then(r => setPipeline(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load(filters);
  }, [filters]); // auto-apply whenever filters change

  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, []);

  /* ── Open add form ─────────────────────────────────── */
  const openAdd = () => {
    setEditCustomer(null);
    setForm({ ...blankForm });
    setShowForm(true);
  };

  /* ── Open edit form ────────────────────────────────── */
  const openEdit = (e: React.MouseEvent, c: any) => {
    e.stopPropagation(); // don't navigate to detail
    setEditCustomer(c);
    setForm({
      customerName:  c.customerName  || "",
      company:       c.company       || "",
      phone:         c.phone         || "",
      email:         c.email         || "",
      notes:         c.notes         || "",
      status:        c.status        || "NEW_LEAD",
      assignedToId:  c.assignedTo?.id || "",
      nextFollowUp:  c.nextFollowUp ? new Date(c.nextFollowUp).toISOString().split("T")[0] : "",
      contactMethod: c.contactMethod || "",
    });
    setShowForm(true);
  };

  /* ── Delete ────────────────────────────────────────── */
  const deleteCustomer = async (e: React.MouseEvent, c: any) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${c.customerName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/crm/${c.id}`);
      toast.success("Customer deleted");
      load(filters);
    } catch { toast.error("Failed to delete customer"); }
  };

  /* ── Submit ────────────────────────────────────────── */
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editCustomer) {
        await api.put(`/crm/${editCustomer.id}`, form);
        toast.success("Customer updated!");
      } else {
        await api.post("/crm", form);
        toast.success("Customer added!");
      }
      setShowForm(false);
      setEditCustomer(null);
      setForm({ ...blankForm });
      load(filters);
    } catch { toast.error("Failed to save customer"); }
  };

  /* inline form JSX — do NOT extract to sub-component (causes focus loss on re-render) */
  const formJSX = (
    <div className="space-y-4 overflow-y-auto max-h-[65vh] pr-1">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
          <input value={form.customerName} onChange={e => setForm(f => ({...f, customerName: e.target.value}))} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
          <input value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up</label>
          <input type="date" value={form.nextFollowUp} onChange={e => setForm(f => ({...f, nextFollowUp: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method</label>
        <div className="flex flex-wrap gap-2">
          {CONTACT_METHODS.map(m => (
            <button key={m.value} type="button"
              onClick={() => setForm(f => ({...f, contactMethod: f.contactMethod === m.value ? "" : m.value}))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                form.contactMethod === m.value ? m.activeColor : m.color + " hover:opacity-80"
              }`}>
              {m.icon} {m.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Employee</label>
        <select value={form.assignedToId} onChange={e => setForm(f => ({...f, assignedToId: e.target.value}))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Select employee...</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">CRM</h2>
        {canCreateCrm && (
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
            <Plus size={16}/> Add Customer
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Leads",      value: stats.totalLeads || 0,                            icon: Users,      color: "text-blue-600"   },
          { label: "Potential Buyers", value: stats.potentialBuyers || 0,                       icon: TrendingUp, color: "text-purple-600" },
          { label: "Deals Won",        value: stats.dealsWon || 0,                              icon: Award,      color: "text-green-600"  },
          { label: "In Negotiation",   value: stats.negotiation || 0,                           icon: TrendingUp, color: "text-orange-600" },
          { label: "Expected Revenue", value: `$${(stats.expectedRevenue || 0).toLocaleString()}`, icon: DollarSign, color: "text-green-700" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <s.icon size={16} className={s.color}/>
              <span className="text-xs text-gray-500">{s.label}</span>
            </div>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {(["customers","pipeline","reports"] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
              activeTab === tab ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
            }`}>
            {tab === "customers" ? "Customers" : tab === "pipeline" ? "Pipeline" : "Reports"}
          </button>
        ))}
      </div>

      {activeTab === "customers" && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <select value={filters.status} onChange={e => setFilters(f => ({...f, status: e.target.value}))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none">
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <input value={filters.company} onChange={e => setFilters(f => ({...f, company: e.target.value}))}
                placeholder="Filter by company..."
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"/>
              <select value={filters.assignedToId} onChange={e => setFilters(f => ({...f, assignedToId: e.target.value}))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none">
                <option value="">All Employees</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <label className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm cursor-pointer">
                <input type="checkbox" checked={filters.followUpToday}
                  onChange={e => setFilters(f => ({...f, followUpToday: e.target.checked}))}/>
                Follow-up Today
              </label>
            </div>

            {/* Contact Method filter — auto-apply */}
            <div>
              <p className="text-xs text-gray-500 mb-2 font-medium">Contact Method</p>
              <div className="flex flex-wrap gap-2">
                <button type="button"
                  onClick={() => setFilters(f => ({...f, contactMethod: ""}))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    !filters.contactMethod
                      ? "bg-gray-700 text-white border-gray-700"
                      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}>All</button>
                {CONTACT_METHODS.map(m => (
                  <button key={m.value} type="button"
                    onClick={() => setFilters(f => ({...f, contactMethod: f.contactMethod === m.value ? "" : m.value}))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      filters.contactMethod === m.value ? m.activeColor : m.color + " hover:opacity-80"
                    }`}>
                    {m.icon} {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Customer","Company","Phone","Email","Contact","Status","Next Follow-up","Last Comment",""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {customers.map(c => (
                  <tr key={c.id}
                    className="hover:bg-blue-50/30 cursor-pointer group"
                    onClick={() => navigate(`/crm/${c.id}`)}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{c.customerName}</td>
                    <td className="px-4 py-3 text-gray-600">{c.company || "-"}</td>
                    <td className="px-4 py-3 text-gray-600">{c.phone || "-"}</td>
                    <td className="px-4 py-3 text-gray-600">{c.email || "-"}</td>
                    <td className="px-4 py-3"><ContactBadge value={c.contactMethod}/></td>
                    <td className="px-4 py-3"><StatusBadge status={c.status}/></td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.nextFollowUp ? new Date(c.nextFollowUp).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 max-w-xs truncate">
                      {c.followUps?.[0]?.comment || c.notes || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {canEditCrm && (
                          <button
                            onClick={e => openEdit(e, c)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                            title="Edit customer"
                          >
                            <Pencil size={13}/>
                          </button>
                        )}
                        {canDeleteCrm && (
                          <button
                            onClick={e => deleteCustomer(e, c)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                            title="Delete customer"
                          >
                            <Trash2 size={13}/>
                          </button>
                        )}
                        <span className="text-blue-600 text-xs font-medium ml-1">View →</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-12 text-gray-400">No customers found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === "pipeline" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {PIPELINE_STAGES.map(stage => {
            const stageCustomers = customers.filter(c => c.status === stage.key);
            return (
              <div key={stage.key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className={`${stage.color} px-4 py-2 flex items-center justify-between`}>
                  <span className="text-white text-sm font-semibold">{stage.label}</span>
                  <span className="bg-white/30 text-white text-xs px-2 py-0.5 rounded-full">{pipeline[stage.key] || 0}</span>
                </div>
                <div className="p-3 space-y-2 min-h-24">
                  {stageCustomers.slice(0,5).map(c => (
                    <div key={c.id} onClick={() => navigate(`/crm/${c.id}`)}
                      className="bg-gray-50 rounded-lg p-2 cursor-pointer hover:bg-blue-50 border border-gray-200">
                      <p className="text-sm font-medium text-gray-800">{c.customerName}</p>
                      {c.company && <p className="text-xs text-gray-500">{c.company}</p>}
                      {c.contactMethod && <ContactBadge value={c.contactMethod}/>}
                    </div>
                  ))}
                  {stageCustomers.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No customers</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "reports" && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Sales Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <p className="text-3xl font-bold text-green-600">{stats.dealsWon || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Deals Won</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-xl">
              <p className="text-3xl font-bold text-red-600">{stats.dealsLost || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Deals Lost</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-xl">
              <p className="text-3xl font-bold text-blue-600">${(stats.totalRevenue || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-600 mt-1">Total Revenue</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <p className="text-3xl font-bold text-purple-600">{stats.converted || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Converted</p>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editCustomer ? `Edit: ${editCustomer.customerName}` : "Add Customer"}
              </h3>
              <button onClick={() => { setShowForm(false); setEditCustomer(null); }}>
                <X size={20} className="text-gray-400"/>
              </button>
            </div>
            <form onSubmit={submit} className="p-5">
              {formJSX}
              <div className="flex gap-3 pt-4 mt-2 border-t border-gray-100">
                <button type="button"
                  onClick={() => { setShowForm(false); setEditCustomer(null); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">
                  {editCustomer ? "Save Changes" : "Add Customer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default CRM;
